import { supabase } from '../lib/supabase';

/**
 * Check if push notifications are supported in the browser
 */
export const isPushNotificationSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

/**
 * Request notification permission from user
 */
export const requestNotificationPermission = async () => {
  if (!isPushNotificationSupported()) {
    return { success: false, error: 'Push notifications not supported' };
  }

  try {
    const permission = await Notification.requestPermission();
    return { success: permission === 'granted', permission };
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Get current notification permission status
 */
export const getNotificationPermission = () => {
  if (!isPushNotificationSupported()) {
    return 'unsupported';
  }
  return Notification.permission;
};

/**
 * Register service worker
 */
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    return { success: false, error: 'Service workers not supported' };
  }

  try {
    const registration = await navigator.serviceWorker?.register('/sw.js', {
      scope: '/'
    });
    
    // Wait for service worker to be ready
    await navigator.serviceWorker?.ready;
    
    return { success: true, registration };
  } catch (error) {
    console.error('Service worker registration failed:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Subscribe to push notifications
 */
export const subscribeToPushNotifications = async (userId) => {
  if (!isPushNotificationSupported()) {
    return { success: false, error: 'Push notifications not supported' };
  }

  try {
    // Check permission
    if (Notification.permission !== 'granted') {
      const permissionResult = await requestNotificationPermission();
      if (!permissionResult?.success) {
        return { success: false, error: 'Permission denied' };
      }
    }

    // Register service worker
    const swResult = await registerServiceWorker();
    if (!swResult?.success) {
      return { success: false, error: swResult?.error };
    }

    const registration = swResult?.registration;

    // Check if already subscribed
    let subscription = await registration?.pushManager?.getSubscription();

    if (!subscription) {
      // Create new subscription
      // Using a dummy VAPID public key - in production, generate your own
      const vapidPublicKey = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib37J8xQmrpcPBblQV4qvOpGq4JJHSgOLwquzNeyeGvqDhfKoMkFflYCkwM';
      
      subscription = await registration?.pushManager?.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
    }

    // Save subscription to database
    const subscriptionData = subscription?.toJSON();
    const { error: dbError } = await supabase?.from('push_subscriptions')?.upsert({
        user_id: userId,
        endpoint: subscriptionData?.endpoint,
        p256dh: subscriptionData?.keys?.p256dh,
        auth: subscriptionData?.keys?.auth,
        subscription_data: subscriptionData
      }, {
        onConflict: 'user_id'
      });

    if (dbError) {
      // PGRST205 is a schema cache error - table exists but PostgREST cache needs refresh
      if (dbError?.code === 'PGRST205') {
        console.warn('Push notification service is initializing. Please try again in a few moments.');
        return { success: false, error: 'Push notification service is initializing. Please try again in a few moments.' };
      }
      console.error('Error saving subscription:', dbError);
      return { success: false, error: dbError?.message };
    }

    return { success: true, subscription: subscriptionData };
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPushNotifications = async (userId) => {
  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker?.ready;
      let subscription = await registration?.pushManager?.getSubscription();
      
      if (subscription) {
        await subscription?.unsubscribe();
      }
    }

    // Remove from database
    const { error: dbError } = await supabase?.from('push_subscriptions')?.delete()?.eq('user_id', userId);

    if (dbError) {
      // PGRST205 is a schema cache error - table exists but PostgREST cache needs refresh
      if (dbError?.code === 'PGRST205') {
        console.warn('Schema cache refreshing - please try again in a few moments');
        return { success: false, error: 'Service is initializing. Please try again shortly.' };
      }
      console.error('Error removing subscription:', dbError);
      return { success: false, error: dbError?.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Check if user is subscribed to push notifications
 */
export const isSubscribedToPushNotifications = async (userId) => {
  try {
    const { data, error } = await supabase?.from('push_subscriptions')?.select('id')?.eq('user_id', userId)?.single();

    if (error && error?.code !== 'PGRST116') {
      // PGRST205 is a schema cache error - table exists but PostgREST cache needs refresh
      if (error?.code === 'PGRST205') {
        console.warn('Schema cache refreshing - push notifications will be available shortly');
        return false; // Return false temporarily until cache refreshes
      }
      console.error('Error checking subscription:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error checking subscription:', error);
    return false;
  }
};

/**
 * Show a local notification (for testing)
 */
export const showLocalNotification = async (title, options = {}) => {
  if (!isPushNotificationSupported()) {
    return { success: false, error: 'Notifications not supported' };
  }

  if (Notification.permission !== 'granted') {
    return { success: false, error: 'Permission not granted' };
  }

  try {
    const registration = await navigator.serviceWorker?.ready;
    await registration?.showNotification(title, {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      vibrate: [200, 100, 200],
      ...options
    });
    return { success: true };
  } catch (error) {
    console.error('Error showing notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Helper function to convert VAPID key
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='?.repeat((4 - base64String?.length % 4) % 4);
  const base64 = (base64String + padding)?.replace(/\-/g, '+')?.replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData?.length; ++i) {
    outputArray[i] = rawData?.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Send push notification via Supabase Edge Function
 */
export const sendPushNotification = async (userId, title, message, data = {}) => {
  try {
    const { data: result, error } = await supabase?.functions?.invoke('send-push-notification', {
      body: {
        userId,
        title,
        message,
        data
      }
    });

    if (error) {
      console.error('Error sending push notification:', error);
      return { success: false, error: error?.message };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending push notification:', error);
    return { success: false, error: error?.message };
  }
};